import _ from "lodash";
import * as Yup from "yup";

export const fieldGroups = [
  {
    title: "loginCredentials",
    items: [
      { label: "username (email)", name: "username", required: true },
      { label: "password", name: "password", required: true },
      { label: "verify password", name: "password2", required: true },
    ],
  },
  {
    title: "companyInformation",
    items: [
      { label: "legalTitle", name: "legal_title", required: true },
      { label: "brandName", name: "name", required: true },
      {
        label: "businessRegistrationNumber",
        name: "registration_code",
        required: true,
      },
      { label: "websiteUrl", name: "url" },
      { label: "aboutUs", name: "about_us" },
    ],
  },
  {
    title: "contactDetails",
    items: [
      { label: "firstName", name: "first_name", required: true },
      { label: "lastName", name: "last_name", required: true },
      { label: "email", name: "email", required: true },
      { label: "mobilePhone", name: "mobile_phone", required: true },
      { label: "phone", name: "phone" },
    ],
  },
  {
    title: "businessAddress",
    items: [
      { label: "address", name: "street", required: true },
      { label: "city", name: "city", required: true },
      { label: "postalCode", name: "postal_code", required: true },
      { label: "regionStateProvince", name: "region", required: true },
      { label: "country", name: "country_code", required: true },
    ],
  },
  {
    title: "Documents",
    items: [
      {
        label: "Certificate of Incorporation",
        name: "certificate_of_incorporation",
        required: true,
        type: "file",
      },
    ],
  },
  {
    title: "Terms & Conditions",
    items: [
      {
        label: "Accept Terms & Conditions",
        name: "terms_and_conditions",
        required: true,
        type: "checkbox",
        default: false,
      },
    ],
  },
];

export function getInitialValues() {
  var initialValues = {};
  fieldGroups.forEach((fieldGroup) => {
    fieldGroup.items.forEach((i) => {
      initialValues[i.name] = i?.["default"] ?? "";
    });
  });
  return initialValues;
}

function getLabelForName(fieldName) {
  const item = fieldGroups.flatMap((group) => group.items).find((item) => item.name === fieldName);

  return item ? item.label : "_" + fieldName;
}

const fileSizeTest = Yup.mixed().test(
  "fileSize",
  "Certificate File too large",
  (value) => !value || (value && value.size <= 2097152), // 2MB
);

export function PasswordRequirements() {
  return (
    <ul style={{ paddingLeft: "0.75rem" }}>
      <li>
        <small>Must have at least 8 characters</small>
      </li>
      <li>
        <small>Must contain at least 1 digit</small>
      </li>
      <li>
        <small>Must contain at least 1 lowercase letter</small>
      </li>
      <li>
        <small>Must contain at least 1 uppercase letter</small>
      </li>
      <li>
        <small>
          Must contain at least 1 special character
          {`(!@#$%^&*(),.?":{}|<>)`}
        </small>
      </li>
    </ul>
  );
}

export const validationSchema = (t, isDocRequired) =>
  Yup.object().shape({
    username: Yup.string()
      .test(
        "is-non-blank",
        `${t(getLabelForName("username"))} ${t("isRequired")}`,
        function (value) {
          if (!value) {
            return false;
          }
          return true;
        },
      )
      .email()
      .test(
        "min-max",
        `${t(getLabelForName("username"))} must be longer than 5 characters`,
        function (value) {
          if (value) {
            return value.length >= 5;
          }
          return true; // If the field is blank, don't return an error
        },
      )
      .test(
        "is-email",
        `${t(getLabelForName("username"))} must be a valid email`,
        function (value) {
          if (value) return (value.split("@")?.[1] ?? "").includes(".");
          return true; //If the field is blank, don't return an error
        },
      ),
    password: Yup.string()
      .required(`${t(getLabelForName("password"))} ${t("isRequired")}`)
      .min(8, `${t(getLabelForName("password"))} must have at least 8 characters`)
      .matches(/[0-9]/, `${t(getLabelForName("password"))} must contain at least 1 digit`)
      .matches(
        /[a-z]/,
        `${t(getLabelForName("password"))} must contain at least 1 lowercase letter`,
      )
      .matches(
        /[A-Z]/,
        `${t(getLabelForName("password"))} must contain at least 1 uppercase letter`,
      )
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        `${t(
          getLabelForName("password"),
        )} must contain at least 1 special character (!@#$%^&*(),.?":{}|<>)`,
      ),
    password2: Yup.string()
      .required(`${t(getLabelForName("password2"))} ${t("isRequired")}`)
      .test(
        "passwords-match",
        `${t(getLabelForName("password2"))} does not match ${t(getLabelForName("password"))}`,
        function (value) {
          return this.parent.password === value;
        },
      ),
    legal_title: Yup.string().test(
      "is-non-blank",
      `${t(getLabelForName("legal_title"))} ${t("isRequired")}`,
      function (value) {
        if (!value) return false;
        return true;
      },
    ),
    name: Yup.string().test(
      "is-non-blank",
      `${t(getLabelForName("name"))} ${t("isRequired")}`,
      function (value) {
        if (!value) {
          return false;
        }
        return true;
      },
    ),
    registration_code: Yup.string().test(
      "is-non-blank",
      `${t(getLabelForName("registration_code"))} ${t("isRequired")}`,
      function (value) {
        if (!value) {
          return false;
        }
        return true;
      },
    ),
    url: Yup.string().url("Invalid URL format"),
    about_us: Yup.string(),
    first_name: Yup.string()
      .test(
        "is-non-blank",
        `${t(getLabelForName("first_name"))} ${t("isRequired")}`,
        function (value) {
          if (!value) {
            return false;
          }
          return true;
        },
      )
      .test(
        "min-max",
        `${t(getLabelForName("first_name"))} must be between 2 and 30 characters`,
        function (value) {
          if (value) {
            return value.length >= 2 && value.length <= 30;
          }
          return true; // If the field is blank, don't return an error
        },
      ),
    last_name: Yup.string()
      .test(
        "is-non-blank",
        `${t(getLabelForName("last_name"))} ${t("isRequired")}`,
        function (value) {
          if (!value) {
            return false;
          }
          return true;
        },
      )
      .test(
        "min-max",
        `${t(getLabelForName("last_name"))} must be between 2 and 30 characters`,
        function (value) {
          if (value) {
            return value.length >= 2 && value.length <= 30;
          }
          return true; // If the field is blank, don't return an error
        },
      ),
    email: Yup.string()
      .email("Invalid email format")
      .required(`${t(getLabelForName("email"))} ${t("isRequired")}`)
      .test(
        "is-same-with-username",
        "email must be the same as username (email)",
        function (value, context) {
          if (value !== context.parent.username) return false;
          return true;
        },
      ),
    mobile_phone: Yup.string().required("Mobile phone is required"),
    // .test(
    //   "one-of-the-phone-numbers-is-required",
    //   `At least one of ${t(getLabelForName("mobile_phone"))} or ${t(
    //     getLabelForName("phone")
    //   )} is required`,
    //   function (value, context) {
    //     if (!value && !context.parent.phone) return false;
    //     return true;
    //   }
    // )
    phone: Yup.string(), // .test(
    //   "one-of-the-phone-numbers-is-required",
    //   `At least one of ${t(getLabelForName("phone"))} or ${t(
    //     getLabelForName("mobile_phone")
    //   )} is required`,
    //   function (value, context) {
    //     if (!value && !context.parent.mobile_phone) return false;
    //     return true;
    //   }
    // )
    street: Yup.string().required(`${t(getLabelForName("street"))} ${t("isRequired")}`),
    city: Yup.string()
      .test("is-non-blank", `${t(getLabelForName("city"))} ${t("isRequired")}`, function (value) {
        if (!value) {
          return false;
        }
        return true;
      })
      .test(
        "min-max",
        `${t(getLabelForName("city"))} must be between 3 and 20 characters`,
        function (value) {
          if (value) {
            return value.length >= 3 && value.length <= 20;
          }
          return true; // If the field is blank, don't return an error
        },
      ),
    postal_code: Yup.string()
      .test(
        "is-non-blank",
        `${t(getLabelForName("postal_code"))} ${t("isRequired")}`,
        function (value) {
          if (!value) {
            return false;
          }
          return true;
        },
      )
      .test(
        "min-max",
        `${t(getLabelForName("postal_code"))} code must be between 3 and 15 characters`,
        function (value) {
          if (value) {
            return value.length >= 3 && value.length <= 15;
          }
          return true; // If the field is blank, don't return an error
        },
      ),
    region: Yup.string()
      .test("is-non-blank", `${t(getLabelForName("region"))} ${t("isRequired")}`, function (value) {
        if (!value) {
          return false;
        }
        return true;
      })
      .test(
        "min-max",
        `${t(getLabelForName("region"))} must be between 5 and 20 characters`,
        function (value) {
          if (value) {
            return value.length >= 5 && value.length <= 20;
          }
          return true; // If the field is blank, don't return an error
        },
      ),
    country_code: Yup.string().required(`${t(getLabelForName("country_code"))} ${t("isRequired")}`),
    certificate_of_incorporation: isDocRequired
      ? Yup.mixed()
          .test(
            "required",
            `${t(getLabelForName("certificate_of_incorporation"))} ${t("isRequired")}`,
            (value) => value && value.size > 0,
          )
          .concat(fileSizeTest)
      : // .concat(fileFormatTest)
        Yup.mixed()
          .notRequired()
          .concat(fileSizeTest), // .concat(fileFormatTest)
    terms_and_conditions: Yup.boolean()
      .required()
      .default(false)
      .test("is-true", `Terms & Condition must by accepted`, function (value) {
        return value === true;
      }),
  });
